import { makeObservable, computed, observable, action, reaction } from 'mobx';

import { IRootStore } from 'shared/entities/store/rootStore';
import { ReactionsHandlerStore } from 'stores/reactionsHandlerStore';
import { FieldModel } from 'shared/models/form';
import {
  MobileChannelsModalTab,
  IChannelModel,
  OpenedChannelKind,
  ChannelKind,
  ConnectedViaJivoChannelKind,
  ConnectedViaWazzupChannelKind
} from 'shared/entities/channels';
import { ScenarioBaseModel } from 'shared/models/scenario';
import { AnalyticsEvent } from 'shared/entities/analytics';
import { IAIBot } from 'shared/entities/smartbotAI';
import { ChannelSelectProps } from 'shared/entities/channelsModal';
import {
  AnyChannelCreationStores,
  WazzupOAuthConnectionStore
} from 'stores/channelCreationStores';

import { mapChannelKindToCreateStore } from './config';
import { ChannelScenariosStore } from './ChannelScenariosStore';

type PendingChannelAction =
  | {
      type: 'select';
      channelId: string;
    }
  | {
      type: 'unselect';
    }
  | {
      type: 'close';
    };

type PrivateFields =
  | '_channelCreationStore'
  | '_pendingChannelAction'
  | '_pendingMobileTab'
  | '_requestChannelChange'
  | '_applyPendingChannelAction'
  | '_selectChannel'
  | '_unselect';

export class ChannelsModalStore extends ReactionsHandlerStore {
  readonly selectedChannelId: FieldModel<string | null> = new FieldModel<
    string | null
  >(null);
  readonly searchScenarios: FieldModel = new FieldModel('');
  readonly selectedChannelKind: FieldModel<OpenedChannelKind | null> =
    new FieldModel<OpenedChannelKind | null>(null);
  readonly channelsModalSelectedMobileTab: FieldModel<MobileChannelsModalTab>;
  readonly connectWithJivoSelectedChannelKind: FieldModel<ConnectedViaJivoChannelKind | null> =
    new FieldModel<ConnectedViaJivoChannelKind | null>(null);

  readonly connectViaWazzupSelectedChannelKind: FieldModel<ConnectedViaWazzupChannelKind | null> =
    new FieldModel<ConnectedViaWazzupChannelKind | null>(null);
  readonly channelScenariosStore: ChannelScenariosStore | null = null;
  /** Подключение Wazzup по кнопке: новый канал, переподключение и перевод с ключа */
  readonly wzOAuth: WazzupOAuthConnectionStore;
  private rootStore: IRootStore;
  private _channelCreationStore: AnyChannelCreationStores | null = null;
  private _pendingChannelAction: PendingChannelAction | null = null;
  private _pendingMobileTab: MobileChannelsModalTab | null = null;
  readonly scenario: ScenarioBaseModel | null;
  readonly bot: IAIBot | null;
  readonly isModal: boolean;

  /**
   * Показывать привязку аккаунтов или нет
   * @type {boolean}
   */
  readonly withAccounts: boolean;
  channelSelectProps: ChannelSelectProps | null;
  constructor({
    rootStore,
    scenario,
    bot,
    withAccounts,
    isModal,
    channelSelectProps,
    initialTab
  }: {
    rootStore: IRootStore;
    scenario: ScenarioBaseModel | null;
    bot?: IAIBot | null;
    withAccounts: boolean;
    isModal: boolean;
    channelSelectProps?: ChannelSelectProps | null;
    initialTab?: MobileChannelsModalTab;
  }) {
    super();
    this.rootStore = rootStore;
    this.scenario = scenario;
    this.withAccounts = withAccounts;
    this.isModal = isModal;
    this.bot = bot ?? null;
    this.channelSelectProps = channelSelectProps ?? null;
    const tab =
      !initialTab || !rootStore.channelsStore.channels.length
        ? MobileChannelsModalTab.createChannel
        : initialTab;
    this.channelsModalSelectedMobileTab =
      new FieldModel<MobileChannelsModalTab>(tab);
    if (!this.scenario && !this.bot) {
      this.channelScenariosStore = new ChannelScenariosStore({
        channelsStore: rootStore.channelsStore,
        channelId: this.selectedChannelId.value
      });
    }

    this.wzOAuth = new WazzupOAuthConnectionStore(rootStore, (channelId) => {
      this.handleChannelCreate(channelId);
      this.selectChannel(channelId);
    });
    this.addReaction({ key: 'wzOAuth', reaction: this.wzOAuth.dispose });

    makeObservable<ChannelsModalStore, PrivateFields>(this, {
      _channelCreationStore: observable.ref,
      _pendingChannelAction: observable,
      _pendingMobileTab: observable,
      channelSelectProps: observable.ref,

      channelCreationStore: computed,
      selectedChannel: computed,
      selectedViaJivoChannelKind: computed,
      selectedViaWazzupChannelKind: computed,
      wazzupOrJivoSelected: computed,
      connectAllChannels: computed,
      selectionLoading: computed,
      showSelectedChannelScenarios: computed,
      pendingNavigationConfirmationOpened: computed,
      editedChannelName: computed,
      pendingChannelChangeIsSelection: computed,

      setChannelCreationStore: action,
      resetChannelCreationStore: action,
      setChannelSelectionProps: action,
      selectChannel: action.bound,
      requestUnselect: action.bound,
      requestClose: action.bound,
      selectTab: action.bound,
      cancelPendingChannelChange: action.bound,
      discardPendingChannelChange: action.bound,
      discardUnsavedScenarios: action.bound,
      saveAndApplyPendingChannelChange: action.bound,
      unselect: action.bound,

      _requestChannelChange: action,
      _applyPendingChannelAction: action,
      _selectChannel: action,
      _unselect: action
    });

    this.addReaction({
      key: 'selectedChannelKind',
      reaction: reaction(
        () => this.selectedChannelKind.value,
        (channelKind) => {
          if (channelKind) {
            this.setChannelCreationStore(channelKind);
          } else {
            this.resetChannelCreationStore();
          }
        }
      )
    });
  }

  get selectedChannel(): IChannelModel | null {
    return !this.selectedChannelId.value
      ? null
      : this.rootStore.channelsStore.channels.getEntity(
          this.selectedChannelId.value
        );
  }

  get channelCreationStore(): AnyChannelCreationStores | null {
    return this._channelCreationStore;
  }

  get connectAllChannels(): boolean {
    return (
      this.channelSelectProps?.connectAllChannels ??
      !!this.scenario?.connectAllChannels
    );
  }

  get selectionLoading(): boolean {
    return this.channelSelectProps?.isLoading ?? false;
  }

  get showSelectedChannelScenarios(): boolean {
    return this.channelScenariosStore !== null;
  }

  get pendingNavigationConfirmationOpened(): boolean {
    return this._pendingChannelAction !== null;
  }

  get editedChannelName(): string | null {
    return this.channelScenariosStore?.editedChannelName ?? null;
  }

  get pendingChannelChangeIsSelection(): boolean {
    return this._pendingChannelAction?.type === 'select';
  }

  setChannelSelectionProps = (
    channelSelectProps: ChannelSelectProps | null | undefined
  ): void => {
    this.channelSelectProps = channelSelectProps ?? null;
  };

  get wazzupOrJivoSelected(): boolean {
    return (
      this.selectedChannelKind.value === ChannelKind.JIVO ||
      this.selectedChannelKind.value === ChannelKind.WZ
    );
  }

  get selectedViaJivoChannelKind(): ConnectedViaJivoChannelKind | null {
    if (this.selectedChannelKind.value !== ChannelKind.JIVO) {
      return null;
    }
    return this.connectWithJivoSelectedChannelKind.value;
  }

  get selectedViaWazzupChannelKind(): ConnectedViaWazzupChannelKind | null {
    if (this.selectedChannelKind.value !== ChannelKind.WZ) {
      return null;
    }
    return this.connectViaWazzupSelectedChannelKind.value;
  }

  get withSections(): boolean {
    if (this.bot) {
      return false;
    }

    return this.isModal
      ? !this.rootStore.uiStore.isMobileSize
      : !this.rootStore.uiStore.isSmallContentSize;
  }

  setChannelCreationStore = (channelKind: ChannelKind): void => {
    this._channelCreationStore = mapChannelKindToCreateStore[channelKind](
      this.rootStore
    );
  };

  resetChannelCreationStore = (): void => {
    this._channelCreationStore = null;
  };

  unselect = (): void => {
    this._unselect();
  };

  selectChannel = (channelId: string): void => {
    if (this.channelScenariosStore) {
      if (
        this.channelScenariosStore.savingStage.isLoading ||
        this.selectedChannelId.value === channelId
      ) {
        return;
      }

      if (!this.rootStore.channelsStore.channels.getEntity(channelId)) {
        return;
      }

      this._requestChannelChange({ type: 'select', channelId });
      return;
    }

    this.selectedChannelId.changeValue(channelId);
  };

  requestUnselect = (): void => {
    if (this.channelScenariosStore) {
      this._requestChannelChange({ type: 'unselect' });
      return;
    }

    this.unselect();
  };

  requestClose = (): boolean => {
    if (!this.channelScenariosStore) {
      return true;
    }

    if (this.channelScenariosStore.savingStage.isLoading) {
      return false;
    }

    if (this.channelScenariosStore.hasChanges) {
      this._requestChannelChange({ type: 'close' });
      return false;
    }

    this._unselect();
    return true;
  };

  cancelPendingChannelChange = (): void => {
    this._pendingChannelAction = null;
    this._pendingMobileTab = null;
  };

  discardPendingChannelChange = (): PendingChannelAction | null => {
    if (
      this.channelScenariosStore?.savingStage.isLoading ||
      !this._pendingChannelAction
    ) {
      return null;
    }

    const pendingChannelAction = this._pendingChannelAction;

    this._applyPendingChannelAction(pendingChannelAction);
    return pendingChannelAction;
  };

  discardUnsavedScenarios = (): void => {
    this.cancelPendingChannelChange();
    this.unselect();
  };

  saveAndApplyPendingChannelChange =
    async (): Promise<PendingChannelAction | null> => {
      const isSaved = await this.channelScenariosStore?.save();
      const pendingChannelAction = this._pendingChannelAction;

      if (!isSaved || !pendingChannelAction) {
        return null;
      }

      this._applyPendingChannelAction(pendingChannelAction);
      return pendingChannelAction;
    };

  unSelectChannelKind = (): void => {
    this.selectedChannelKind.changeValue(null);
    this.connectWithJivoSelectedChannelKind.changeValue(null);
    this.connectViaWazzupSelectedChannelKind.changeValue(null);
    this.resetChannelCreationStore();
  };

  handleChannelCreate = (channelId: string): void => {
    if (!this.withSections) {
      this.selectTab(MobileChannelsModalTab.selectChannels);

      if (this.bot) {
        this.channelSelectProps?.onChange({ value: true, channelId });
      }
    }

    this.unSelectChannelKind();
  };

  changeConnectAllChannels = (value: boolean): void => {
    const onConnectAllChannelsChange =
      this.channelSelectProps?.onConnectAllChannelsChange;

    if (onConnectAllChannelsChange) {
      onConnectAllChannelsChange(value);
      return;
    }

    if (!this.scenario) {
      return;
    }

    this.scenario.setChannelsIds(
      value ? this.rootStore.channelsStore.channels.keys : []
    );
  };

  selectChannelViaJivo = (kind: ConnectedViaJivoChannelKind | null) => {
    if (kind) {
      this.rootStore.analyticsStore.sendEvent(
        AnalyticsEvent.creationChannelViaJivoOpenForm,
        {
          channel_kind_via_jivo: kind
        }
      );
    }
    this.selectedChannelKind.changeValue(kind ? ChannelKind.JIVO : null);
    this.connectWithJivoSelectedChannelKind.changeValue(kind);
  };

  selectChannelViaWazzup = (kind: ConnectedViaWazzupChannelKind | null) => {
    if (kind) {
      this.rootStore.analyticsStore.sendEvent(
        AnalyticsEvent.creationChannelViaWazzupOpenForm,
        {
          channel_kind_via_wazzup: kind
        }
      );
    }
    this.selectedChannelKind.changeValue(kind ? ChannelKind.WZ : null);
    this.connectViaWazzupSelectedChannelKind.changeValue(kind);
  };

  isSelectedChannel = (
    via: ChannelKind.JIVO | ChannelKind.WZ,
    channelKind: ChannelKind
  ): boolean => {
    if (via === ChannelKind.JIVO) {
      return this.selectedViaJivoChannelKind === channelKind;
    }
    return this.selectedViaWazzupChannelKind === channelKind;
  };

  selectTab = (tab: MobileChannelsModalTab) => {
    if (this.channelScenariosStore) {
      if (this.channelScenariosStore.savingStage.isLoading) {
        return;
      }

      const isCurrentTab = this.channelsModalSelectedMobileTab.value === tab;

      if (this.channelScenariosStore.hasChanges) {
        this._pendingMobileTab = isCurrentTab ? null : tab;
        this._requestChannelChange({ type: 'unselect' });
        return;
      }

      if (!isCurrentTab) {
        this.channelsModalSelectedMobileTab.changeValue(tab);
      }

      this._unselect();
      return;
    }

    this.channelsModalSelectedMobileTab.changeValue(tab);
    this.unselect();
  };

  private _requestChannelChange(
    pendingChannelAction: PendingChannelAction
  ): void {
    if (this.channelScenariosStore?.savingStage.isLoading) {
      return;
    }

    if (!this.channelScenariosStore?.hasChanges) {
      this._applyPendingChannelAction(pendingChannelAction);
      return;
    }

    this._pendingChannelAction = pendingChannelAction;
  }

  private _applyPendingChannelAction(
    pendingChannelAction: PendingChannelAction
  ): void {
    const pendingMobileTab = this._pendingMobileTab;

    this._pendingChannelAction = null;
    this._pendingMobileTab = null;

    if (pendingChannelAction.type === 'select') {
      this._selectChannel(pendingChannelAction.channelId);
      return;
    }

    this._unselect();

    if (pendingMobileTab) {
      this.channelsModalSelectedMobileTab.changeValue(pendingMobileTab);
    }
  }

  private _selectChannel(channelId: string): void {
    const channel = this.rootStore.channelsStore.channels.getEntity(channelId);

    if (!channel) {
      return;
    }

    this.selectedChannelId.changeValue(channelId);
    this.channelScenariosStore?.setChannel(channelId);
  }

  private _unselect(): void {
    this.selectedChannelId.changeValue(null);
    this.channelScenariosStore?.setChannel(null);
  }
}
